import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRight } from 'lucide-react';
import { getContactData } from '../../../Public/contact-data.js';
import * as authActions from '../../../Redux/Auth/AuthActions.js';
import ContactService from '../../../Services/ContactService.js';
import { getLandingUrl } from '../../../Hooks/useUtmCapture';

// Custom Components
import Selector from '../../Select/select-component.js';
import { InputComponent, TextAreaComponent } from '../../Inputs/input-component.js';

// --- Helpers ---
const isOtherValue = (val) => {
    if (!val) return false;
    const s = String(val).trim().toLowerCase();
    return s === 'other' || s === 'אחר';
};

const ContactForm = ({ onSuccess }) => {
    const dispatch = useDispatch();

    // 1. Global State
    const { language, dir } = useSelector((state) => state.systemData || {});

    // 2. Data Source
    const data = getContactData(language);
    const txt = data.form;
    const topics = data.topics;

    // 3. Local State
    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [agreeMarketing, setAgreeMarketing] = useState(true);
    const [status, setStatus] = useState('idle');
    const consent = txt.consent || {};

    useEffect(() => {
        dispatch(authActions.isLoggedIn());
    }, [dispatch]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email && !formData.phone) return;
        // Terms of Service must be accepted before we submit.
        if (!agreeTerms) {
            setStatus('needsTerms');
            return;
        }

        setStatus('loading');
        const { email, fullName, phone, subject, message } = formData;

        const otherSelected = isOtherValue(subject);
        const finalSubject = otherSelected ? 'General Inquiry' : (subject || 'General Inquiry');

        try {
            // Flowma backend, creates internal lead + sends email.
            // preferredContact carries the actual consent so it syncs to Flowma
            // (marketing → email & SMS campaign opt-in; terms → ToS accepted).
            await ContactService.sendContact({
                fullName: String(fullName || '').trim(),
                email,
                phone,
                subject: finalSubject,
                message,
                leadSource: txt.leadSource,
                preferredContact: { marketing: agreeMarketing, terms: agreeTerms },
                page_url: getLandingUrl(),
            });

            setStatus('success');
            if (onSuccess) onSuccess();
            setFormData({ email: '', fullName: '', phone: '', subject: '', message: '' });
            setAgreeMarketing(false);
            setAgreeTerms(false);
            setTimeout(() => setStatus('idle'), 3000);
        } catch (error) {
            console.error('Form submission error:', error);
            setStatus('idle');
        }
    };

    return (
        <div dir={dir} className="flex w-full items-center justify-center relative">
            <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit}>

                {/* Header */}
                <div className="flex flex-col gap-1.5 text-start mb-1">
                    <h2 className="font-secondary text-3xl lg:text-4xl font-extrabold leading-[0.95] tracking-[-0.02em] text-neutral-900">
                        {txt.title}
                    </h2>
                    <p className="text-base md:text-lg text-neutral-500">
                        {txt.subtitle}
                    </p>
                </div>

                {/* Input Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">

                    {/* Name */}
                    <InputComponent
                        size="lg"
                        className="!bg-neutral-100 !border-transparent !rounded-2xl focus:!border-secondary-500 focus:!bg-white"
                        placeholder={txt.placeholders.fullName}
                        value={formData.fullName}
                        onChange={(e) => handleChange('fullName', e.target.value)}
                    />

                    {/* Email */}
                    <InputComponent
                        size="lg"
                        type="email"
                        dir="ltr"
                        className={`!bg-neutral-100 !border-transparent !rounded-2xl focus:!border-secondary-500 focus:!bg-white ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                        placeholder={txt.placeholders.email}
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                    />

                    {/* Phone (optional) */}
                    <div className="md:col-span-2">
                        <InputComponent
                            size="lg"
                            type="tel"
                            className="!bg-neutral-100 !border-transparent !rounded-2xl focus:!border-secondary-500 focus:!bg-white"
                            placeholder={txt.placeholders.phone}
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                        />
                    </div>

                    {/* Topic Selector */}
                    <div className="md:col-span-2">
                        <Selector
                            options={topics}
                            value={formData.subject}
                            placeholder={txt.placeholders.selectService}
                            onChange={(val) => handleChange('subject', val)}
                            size="lg"
                            className="w-full"
                            triggerClassName="!rounded-2xl !bg-neutral-100 !border-transparent focus:!border-secondary-500"
                        />
                    </div>

                    {/* Message */}
                    <div className="md:col-span-2">
                        <TextAreaComponent
                            size="lg"
                            rows={4}
                            className="!bg-neutral-100 !border-transparent !rounded-2xl focus:!border-secondary-500 focus:!bg-white"
                            placeholder={txt.placeholders.message}
                            value={formData.message}
                            onChange={(e) => handleChange('message', e.target.value)}
                        />
                    </div>

                </div>

                {/* Consent */}
                <div className="flex flex-col gap-3 -mt-1">
                    <label className="flex items-start gap-3 cursor-pointer text-start">
                        <input
                            type="checkbox"
                            checked={agreeMarketing}
                            onChange={(e) => setAgreeMarketing(e.target.checked)}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-secondary-500 cursor-pointer"
                        />
                        <span className="text-sm text-neutral-600 leading-snug">
                            {consent.marketing}
                        </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer text-start">
                        <input
                            type="checkbox"
                            required
                            checked={agreeTerms}
                            onChange={(e) => { setAgreeTerms(e.target.checked); if (e.target.checked && status === 'needsTerms') setStatus('idle'); }}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-secondary-500 cursor-pointer"
                        />
                        <span className="text-sm text-neutral-600 leading-snug">
                            {consent.termsLink ? (
                                <>
                                    {consent.terms}{' '}
                                    <a href={consent.termsLink} target="_blank" rel="noopener noreferrer" className="text-secondary-600 font-semibold underline">
                                        {language === 'he' ? 'לצפייה' : 'View'}
                                    </a>
                                </>
                            ) : consent.terms}
                            <span className="text-red-500"> *</span>
                        </span>
                    </label>

                    {status === 'needsTerms' && (
                        <p className="text-sm font-semibold text-red-500">{consent.required}</p>
                    )}
                </div>

                {/* CTA */}
                <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="group mt-2 w-full flex items-center justify-center gap-3 rounded-full bg-secondary-500 hover:bg-secondary-600 text-white font-bold uppercase tracking-wide text-lg md:text-xl py-5 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <span>{status === 'loading' ? txt.sendingLabel : txt.submitLabel}</span>
                    <ArrowRight className={`h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 ${dir === 'rtl' ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                </button>

            </form>
        </div>
    );
};

export default ContactForm;
